/**
 * Module-level store of the attachment transfers in flight right now, keyed by
 * a per-transfer id. Writers: `useAttachmentUpload` (sending bytes) and
 * `useAttachmentDownload` (assembling a chunked version). Reader:
 * `AttachmentList`, which swaps a file's row for `AttachmentTransferRow` while
 * that file has a transfer.
 *
 * Progress lives here rather than in React state because a 1.8 GB upload ticks
 * many times a second: mutating a leaf re-renders the one row that reads it,
 * where the hook state it replaces re-rendered the whole panel.
 *
 * Two invariants make that granularity real. `fileUid` is fixed at start and
 * never rewritten — a new file's transfer stays `null` for its whole life, so a
 * reader mapping fileUid to transfer id touches no leaf that progress mutates.
 * And cancel and retry handles are held in plain Maps, never in the proxy: a
 * `UploadTask` or an `AbortController` deep-proxied by Valtio misbehaves, and
 * nothing renders from one.
 *
 * Nothing here makes `fileUid` unique: a batch holding two files of the same
 * name resolves both against the same existing attachment and opens two
 * transfers over one fileUid. `AttachmentList` therefore groups by fileUid and
 * renders every transfer it finds, rather than assuming one.
 *
 * A transfer leaves the store three ways. It completes, the reader cancels it,
 * or it fails — and only the third keeps its row, because a failure is the one
 * ending the reader can still act on.
 */
import { proxy } from 'valtio';

/**
 * What a transfer is moving. `upload` sends a file's bytes to Storage and knows
 * its total; `assemble` pulls a chunked version's chunks into memory and reports
 * only what it holds, because a partial assembly is not a partial download.
 */
export type AttachmentTransferKind = 'upload' | 'assemble';

/**
 * What Cancel can do with a transfer right now. `pending` is the window between
 * opening the row and being handed something to stop — a reservation call still
 * in flight — and it is not the same fact as `never`, which is a
 * non-resumable `uploadBytes` or a transfer that has already failed. Telling the
 * two apart is the whole reason this is not a boolean: a reader looking at a
 * perfectly cancellable upload must not be told it can never be stopped.
 */
export type AttachmentTransferCancellation = 'pending' | 'ready' | 'never';

/** One transfer in flight. */
export type AttachmentTransfer = {
    /** Identity in `transfers` and in `cancelHandles`; minted by the writer. */
    transferId: string;
    /**
     * The attachment this transfer concerns, or null when it is a brand-new
     * file that has no row in the panel yet. Fixed for the transfer's life.
     */
    fileUid: string | null;
    /** Shown on the row, so four concurrent uploads are told apart. */
    fileName: string;
    kind: AttachmentTransferKind;
    /** Bytes sent, or bytes held in memory for an assembly. */
    bytesTransferred: number;
    /** Total bytes expected, or 0 when the writer does not know one. */
    totalBytes: number;
    /**
     * Whether the row offers Cancel, and if not, whether it ever will. Every
     * transfer opens `pending`; `allowCancel` moves it to `ready` and
     * `denyCancel` to `never`. The writer cannot state it at start, because a
     * resumable upload only learns its handle after the reservation returns.
     */
    cancellation: AttachmentTransferCancellation;
    /**
     * One sentence saying why the transfer stopped, or null while it is still
     * moving. Set only through `failTransfer`, which also registers the retry
     * the row offers, so a transfer that carries a failure always carries a way
     * to run it again.
     */
    failure: string | null;
};

export type AttachmentTransfersState = {
    transfers: Record<string, AttachmentTransfer>;
};

export const attachmentTransfersState: AttachmentTransfersState = proxy<AttachmentTransfersState>({
    transfers: {},
});

const cancelHandles = new Map<string, () => void>();
const retryHandles = new Map<string, () => void>();

/**
 * Opens a row. A transfer always starts unfailed and always starts with Cancel
 * pending, so the writer cannot say otherwise: both facts are established by
 * what the writer does next, not by what it claims here.
 */
const startTransfer = (transfer: Omit<AttachmentTransfer, 'failure' | 'cancellation'>) => {
    attachmentTransfersState.transfers[transfer.transferId] = {
        ...transfer,
        cancellation: 'pending',
        failure: null,
    };
};

const reportBytes = (transferId: string, bytesTransferred: number, totalBytes?: number) => {
    const transfer = attachmentTransfersState.transfers[transferId];
    if (!transfer) {
        return;
    }
    transfer.bytesTransferred = bytesTransferred;
    if (totalBytes !== undefined) {
        transfer.totalBytes = totalBytes;
    }
};

const allowCancel = (transferId: string, cancel: () => void) => {
    const transfer = attachmentTransfersState.transfers[transferId];
    if (!transfer) {
        return;
    }
    cancelHandles.set(transferId, cancel);
    transfer.cancellation = 'ready';
};

/**
 * Settles that no handle is coming. Called by a writer that has committed to a
 * primitive it cannot stop — a non-resumable `uploadBytes` — so the row can say
 * "cannot be stopped" rather than leaving a reader waiting for a Cancel that
 * will never light up.
 */
const denyCancel = (transferId: string) => {
    const transfer = attachmentTransfersState.transfers[transferId];
    if (!transfer) {
        return;
    }
    transfer.cancellation = 'never';
};

const endTransfer = (transferId: string) => {
    cancelHandles.delete(transferId);
    retryHandles.delete(transferId);
    delete attachmentTransfersState.transfers[transferId];
};

/**
 * Keeps the row and says why it stopped. `retry` is required, not optional: a
 * failed row's whole reason to exist is the action it offers, so a writer that
 * cannot offer one calls `endTransfer` and reports the failure some other way.
 * The cancel handle goes first — a task that has already failed has nothing
 * left to stop — which is what disables the Cancel the row still renders.
 */
const failTransfer = (transferId: string, failure: string, retry: () => void) => {
    const transfer = attachmentTransfersState.transfers[transferId];
    if (!transfer) {
        return;
    }
    cancelHandles.delete(transferId);
    retryHandles.set(transferId, retry);
    transfer.cancellation = 'never';
    transfer.failure = failure;
};

/**
 * Drops the row first and calls the handle second, so the panel answers the
 * click even if the underlying task takes its time noticing. Cancelling a
 * transfer that holds no handle only drops the row, which is why `cancellation`
 * gates the button.
 */
const cancelTransfer = (transferId: string) => {
    const cancel = cancelHandles.get(transferId);
    endTransfer(transferId);
    cancel?.();
};

/**
 * Drops the failed row and runs the retry it was holding. The retry opens a
 * transfer of its own, so the reader sees the same file start again rather than
 * the old row change its mind.
 */
const retryTransfer = (transferId: string) => {
    const retry = retryHandles.get(transferId);
    endTransfer(transferId);
    retry?.();
};

export const attachmentTransfersActions = Object.freeze({
    startTransfer,
    reportBytes,
    allowCancel,
    denyCancel,
    cancelTransfer,
    failTransfer,
    retryTransfer,
    endTransfer,
});
